import React, { createContext, useContext, useEffect, useState } from 'react';
import { isLocalDemo, supabase } from '@/lib/supabase';
import { User, Session } from '@supabase/supabase-js';

import { MusicianProfile } from '@/types';

interface SignUpResult {
  user: User;
  profile?: MusicianProfile | null;
  requiresEmailConfirmation?: boolean;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: MusicianProfile | null;
  loading: boolean;
  signUp: (email: string, password: string, userData: { name?: string; location?: string; bio?: string; profile_image?: string }) => Promise<SignUpResult>;
  signIn: (email: string, password: string) => Promise<{ user: User; session: Session }>;
  signOut: () => Promise<void>;
  refetchProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<MusicianProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = async (userId: string, retries = 3, delayMs = 500): Promise<MusicianProfile | null> => {
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        const { data, error } = await supabase
          .from('musician_profiles')
          .select('*')
          .eq('user_id', userId)
          .maybeSingle();

        if (!error && data) {
          const musicianProfile = data as MusicianProfile;
          setProfile(musicianProfile);
          return musicianProfile;
        }

        // If it's the last attempt or there's an error, don't wait and log/return
        if (error && attempt === retries) {
          console.error('Error fetching profile:', error);
        }
      } catch (error) {
        if (attempt === retries) {
          console.error('Error fetching profile in catch:', error);
        }
      }

      if (attempt < retries) {
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }
    }

    setProfile(null);
    return null;
  };

  useEffect(() => {
    if (isLocalDemo) {
      const demoUser = {
        id: 'local-demo-user',
        email: 'demo@resuena.local',
        user_metadata: { name: 'Juan García' },
      } as User;
      const demoProfile = {
        id: 'local-demo-profile',
        user_id: demoUser.id,
        username: 'juan.demo',
        display_name: 'Juan García',
        bio: 'Productor y guitarrista en modo demo local.',
        location: 'Buenos Aires',
        avatar_url: null,
        years_experience: 8,
        experience_level: 'advanced',
        hourly_rate: null,
        availability: 'Noches y fines de semana',
        instruments: ['Guitarra', 'Producción'],
        rating: 4.8,
        total_ratings: 24,
        created_at: new Date().toISOString(),
        available_for_collab: true,
        available_for_gigs: true,
        onboarding_complete: true,
        role: 'user',
      } as MusicianProfile;

      setUser(demoUser);
      setSession({ user: demoUser } as Session);
      setProfile(demoProfile);
      setLoading(false);
      return;
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
      } else {
        setLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id).finally(() => setLoading(false));
      } else {
        setProfile(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (
    email: string,
    password: string,
    userData: { name?: string; location?: string; bio?: string; profile_image?: string }
  ): Promise<SignUpResult> => {
    // 1. Create the auth user
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/login`,
        data: {
          name: userData.name,
          location: userData.location,
          bio: userData.bio,
        },
      },
    });

    if (authError) throw authError;
    if (!authData.user) {
      throw new Error('User creation failed unexpectedly');
    }

    // 2. Profile creation is handled by the auth.users database trigger.
    // Client-side inserts can run without a session when email confirmation is
    // enabled, which causes RLS failures on musician_profiles.
    let profileData: MusicianProfile | null = null;

    // 3. If no email confirmation is required, keep the returned session.
    if (authData.session) {
      setUser(authData.user);
      setSession(authData.session);
      profileData = await fetchProfile(authData.user.id);

      return {
        user: authData.user,
        profile: profileData,
        requiresEmailConfirmation: false,
      };
    }

    // 4. Development bypass: production Auth may still return session=null
    // while the temporary DB trigger autoconfirms the user. Try a normal login
    // once before falling back to the verify-email screen.
    const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (!signInError && signInData.user && signInData.session) {
      setUser(signInData.user);
      setSession(signInData.session);
      profileData = await fetchProfile(signInData.user.id);

      return {
        user: signInData.user,
        profile: profileData,
        requiresEmailConfirmation: false,
      };
    }

    return {
      user: authData.user,
      profile: profileData,
      requiresEmailConfirmation: true,
    };
  };

  const signIn = async (email: string, password: string) => {
    if (isLocalDemo && user && session) {
      return { user, session };
    }

    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    if (isLocalDemo) return;
    await supabase.auth.signOut();
  };

  const refetchProfile = async () => {
    if (user) {
      await fetchProfile(user.id);
    }
  };

  return (
    <AuthContext.Provider value={{ user, session, profile, loading, signUp, signIn, signOut, refetchProfile }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
